import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  remoteConnectShellQuery,
  saveRemoteConnect,
  type RemoteConnectConfig,
  type RemoteConnectShell,
} from '../../api/settingsRemoteConnect'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './RustdeskOrgPanels.css'

/**
 * Settings -> AGENT SETTINGS -> Remote Connect, bifa cu bifa fata de
 * `views/version-3/settings/remote_connect/company_settings.php`.
 *
 * ASTA E FILA SCREENCONNECT. Ecranul RustDesk sta langa ea in bara si isi are
 * comutatorul lui; ce se apasa aici nu-l atinge.
 *
 * RUSTDESK NU MAI E AICI. Pana pe 6 octombrie 2026 sub bife statea si RustDesk
 * pe organizatie (`RustdeskOrgPanels`); s-a mutat in meniul din stanga al
 * setarilor, Settings -> General -> RustDesk (sub AI Agent), langa setarile
 * furnizorului ("RustDesk servers"). Aici ramane o linie care spune unde. Api-ul trimite in continuare
 * `rustdesk` in ecranul asta - de acolo il citeste fila noua.
 *
 * BUTONUL DE SALVARE LIPSESTE CAND OMUL N-ARE DREPTUL. In portal el se vedea
 * intotdeauna, fiindca scrierea cerea acelasi drept ca citirea - iar dreptul ala
 * il avea si cine n-avea ce cauta la deschiderea accesului de la distanta catre
 * toate statiile firmei.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

export function RemoteConnectPage() {
  const query = useQueryClient()
  const navigate = useNavigate()
  const shell = useQuery(remoteConnectShellQuery)
  const [live, setLive] = useState<RemoteConnectShell | null>(null)
  const [config, setConfig] = useState<RemoteConnectConfig | null>(null)
  const [busy, setBusy] = useState(false)

  const page = live ?? shell.data ?? null

  useEffect(() => {
    if (page && !config) setConfig(page.settings)
  }, [page, config])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page || !config) return <Spinner />

  const locked = !page.canWrite

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    /* Bifele nebifate NU se trimit, ca in formularul vechi. */
    const fields: Record<string, string> = {}
    if (config.remote_connect_access) fields.remote_connect_access = '1'
    if (config.enable_technician_remote_control) fields.enable_technician_remote_control = '1'

    void saveRemoteConnect(fields)
      .then((next) => {
        setBusy(false)
        setLive(next)
        setConfig(next.settings)
        void query.invalidateQueries({ queryKey: ['settings', 'remote_connect'] })
        toast('Settings saved', 'Remote Connect')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Remote Connect', 'error')
      })
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {page.tabs.length > 0 ? <li className="menu-header">{page.tabs[0].group}</li> : null}
          {page.tabs.map((tab) => (
            <li className="menu-tab active" key={tab.id}>
              <a id={tab.id} href={`/app/${tab.href}`} onClick={(e) => e.preventDefault()}>
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        <div id="content">
          <h3 style={{ marginTop: 0 }}>Remote Connect</h3>

          <div className="row">
            <div className="col-md-6">
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">Remote Access</h3>
                </div>
                <form id="remote-connect-settings" onSubmit={submit}>
                  <table className="table table-hover" width="100%">
                    <tbody>
                      <tr>
                        <td className="pl-2">
                          <label htmlFor="rcAll" style={{ fontWeight: 'normal', marginBottom: 0 }}>
                            Enable access for all devices
                          </label>
                        </td>
                        <td className="pr-2 align-right">
                          <input
                            type="checkbox"
                            id="rcAll"
                            checked={config.remote_connect_access}
                            disabled={locked}
                            onChange={(e) => setConfig({ ...config, remote_connect_access: e.target.checked })}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="pl-2">
                          <label htmlFor="rcTech" style={{ fontWeight: 'normal', marginBottom: 0 }}>
                            Enable technician remote control
                          </label>
                        </td>
                        <td className="pr-2 align-right">
                          <input
                            type="checkbox"
                            id="rcTech"
                            checked={config.enable_technician_remote_control}
                            disabled={locked}
                            onChange={(e) =>
                              setConfig({ ...config, enable_technician_remote_control: e.target.checked })
                            }
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {page.canWrite ? (
                    <div className="rd-org-actions p-2" style={{ marginTop: 0 }}>
                      <button className="btn btn-success" type="submit" disabled={busy}>
                        Save
                      </button>
                    </div>
                  ) : null}
                </form>
              </div>
            </div>
          </div>

          <p className="rd-moved" id="rustdesk-moved">
            <i className="fas fa-info-circle" aria-hidden="true"></i> RustDesk settings moved to{' '}
            <a
              href="/app/settings/rustdesk"
              onClick={(e) => {
                e.preventDefault()
                void navigate({ to: '/settings/rustdesk' })
              }}
            >
              Settings &rsaquo; General &rsaquo; RustDesk
            </a>
            .
          </p>
        </div>
      </div>
    </>
  )
}
